import {
  ArrowsDownUpIcon,
  CaretDownIcon,
  MagnifyingGlassIcon,
  SlidersHorizontalIcon,
} from "@phosphor-icons/react";
import { times } from "lodash-es";
import { useTranslations } from "use-intl";

import { formatHeaderDate } from "@/app/lib/utils/dates";
import { FilterButton, FilterButtonSkeleton } from "@/app/ui/FilterButton";
import { HeaderSearchButton } from "@/app/ui/HeaderSearchButton";
import { StickyHeader } from "@/components/StickyHeader/StickyHeader";
import { HotelsListDrawerComponent } from "@/features/hotels/components/hotels-list/types";
import { useHotelSearchDataContext } from "@/features/hotels/contexts/HotelSearchDataContext";
import type {
  HotelsSortValue,
  IHotelsFilters,
} from "@/features/hotels/types/types";
import { HotelsSortOptions } from "@/types/enums";

type Props = {
  onFilterButtonClick: (drawerComponent: HotelsListDrawerComponent) => void;
  selectedSortValue: HotelsSortValue;
  progress?: number;
  onSearchDrawerOpen: () => void;
  activeFilters:
    | (Partial<Record<keyof IHotelsFilters, boolean>> & {
        searchByName: boolean;
      })
    | null;
};

export function HotelListHeader({
  onFilterButtonClick,
  selectedSortValue,
  progress,
  activeFilters,
  onSearchDrawerOpen,
}: Props) {
  const { searchData } = useHotelSearchDataContext();
  const t = useTranslations();

  const sortOptionsLabels = {
    [HotelsSortOptions.PRICE]: t("hotels.filters.cheapestFirst"),
    [HotelsSortOptions.RATING]: t("hotels.filters.ratingFirst"),
    [HotelsSortOptions.RECOMMENDED]: t("hotels.filters.recommendedFirst"),
    [HotelsSortOptions.SPECIALDEAL]: t("hotels.filters.specialDealFirst"),
  };

  return (
    <StickyHeader>
      <div className="flex items-center justify-between gap-2 py-2 text-text-primary">
        <div className="grow overflow-hidden">
          <HeaderSearchButton
            onClick={() => {
              onSearchDrawerOpen();
            }}
            progress={
              progress != null
                ? { variant: "determinate", value: progress }
                : undefined
            }
          >
            <h3 className="w-full truncate text-start font-medium text-text-title">
              {searchData?.destination?.name}
            </h3>

            <div className="w-full truncate text-xs font-normal text-text-tertiary">
              {[
                `${formatHeaderDate(searchData!.dates.checkIn)} - ${formatHeaderDate(searchData!.dates.checkOut)}`,
                t("hotels.search.roomCount", {
                  count: searchData!.rooms.length,
                }),
              ].map((item, index) => (
                <span key={index}>
                  {index > 0 ? <> &bull; </> : null}
                  {item}
                </span>
              ))}
            </div>
          </HeaderSearchButton>
        </div>
      </div>

      <div className="mb-2 flex gap-1 overflow-x-scroll pt-px pb-2">
        {progress !== undefined ? (
          times(4, (idx) => <FilterButtonSkeleton key={idx} />)
        ) : (
          <>
            <FilterButton
              onClick={() =>
                onFilterButtonClick(HotelsListDrawerComponent.SORT)
              }
            >
              {sortOptionsLabels[selectedSortValue.field]}
              <ArrowsDownUpIcon
                size={20}
                className="fill-text-primary"
                aria-hidden
              />
            </FilterButton>
            <FilterButton
              onClick={() =>
                onFilterButtonClick(HotelsListDrawerComponent.ALL_FILTERS)
              }
              active={Object.values(activeFilters || {}).some(Boolean)}
            >
              {t("hotels.filters.allFilters")}
              <SlidersHorizontalIcon
                size={20}
                className="fill-text-primary"
                aria-hidden
              />
            </FilterButton>
            <FilterButton
              onClick={() =>
                onFilterButtonClick(HotelsListDrawerComponent.SEARCH_BY_NAME)
              }
              active={activeFilters?.searchByName || false}
            >
              {t("hotels.filters.searchByName")}
              <MagnifyingGlassIcon
                aria-hidden
                size={20}
                className="fill-text-primary"
              />
            </FilterButton>
            <FilterButton
              onClick={() => {
                onFilterButtonClick(HotelsListDrawerComponent.HOTEL_CLASS);
              }}
              active={activeFilters?.starRating || false}
            >
              {t("hotels.filters.hotelClass")}
              <CaretDownIcon size={20} aria-hidden />
            </FilterButton>
          </>
        )}
      </div>
    </StickyHeader>
  );
}
